import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import type { Octokit } from "@octokit/rest";
import { notify } from "@/lib/toast";

export interface RateLimitSummary {
  remaining: number;
  limit: number;
  resetAt: Date;
  lowBudget: boolean; // remaining < 200
  exhausted: boolean; // remaining < 20
}

/**
 * Polls the GitHub rate limit every 60 s.
 *
 * When `remaining` drops below 20 (critical threshold), a toast is fired once
 * per crossing (dedupe key prevents retry-storm duplicates).
 * The banner in rate-limit-banner.tsx handles the persistent low-budget signal
 * (< 200) — these two signals target different thresholds intentionally.
 */
export function useRateLimit(octokit: Octokit | null) {
  // Track whether we already fired the critical toast in this session so we
  // only emit it on the first crossing, not on every poll tick.
  const criticalEmitted = useRef(false);

  return useQuery<RateLimitSummary>({
    queryKey: ["rate_limit", octokit ? "auth" : "anon"],
    enabled: octokit != null,
    refetchInterval: 60_000,
    queryFn: async () => {
      if (!octokit) throw new Error("no octokit");
      const res = await octokit.rest.rateLimit.get();
      const core = res.data.resources.core;
      const remaining = core.remaining;
      const resetAt = new Date(core.reset * 1000);

      if (remaining < 20 && !criticalEmitted.current) {
        criticalEmitted.current = true;
        const resetTime = format(resetAt, "HH:mm");
        notify.error("Rate limit critical", {
          description: `Only ${remaining} requests left. Resets at ${resetTime}.`,
          dedupeKey: "rate-limit-critical",
        });
      } else if (remaining >= 20) {
        // Reset the flag if budget recovered (e.g. after reset window).
        criticalEmitted.current = false;
      }

      return {
        remaining,
        limit: core.limit,
        resetAt,
        lowBudget: remaining < 200,
        exhausted: remaining < 20,
      } satisfies RateLimitSummary;
    },
  });
}
